import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import PostCard from '../components/PostCard';
import ReputationCard from '../components/ReputationCard';
import {
  Mail,
  Calendar,
  FileText,
  Award,
  TrendingUp,
  Loader2,
  Shield
} from 'lucide-react';

export default function UserProfilePage() {
  const { userId } = useParams();
  const [userProfile, setUserProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState({
    totalPosts: 0,
    cfeReports: 0,
    blogPosts: 0,
    totalLikes: 0,
    verifiedReports: 0
  });

  useEffect(() => {
    if (!userId) {
        setLoading(false);
        return;
    }

    const fetchUserData = async () => {
        setLoading(true);
        
        const userDocRef = doc(db, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
        } else {
            console.error("No such user document!");
            setLoading(false);
            return;
        }

        const q = query(
          collection(db, 'posts'),
          where('authorId', '==', userId),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const postsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPosts(postsData);
        setUserStats({
          totalPosts: postsData.length,
          cfeReports: postsData.filter(p => p.type === 'CFE').length,
          blogPosts: postsData.filter(p => p.type !== 'CFE').length,
          totalLikes: postsData.reduce((sum, post) => sum + (post.likes?.length || 0), 0),
          verifiedReports: postsData.filter(p => p.verified).length
        });
        setLoading(false);
    };

    fetchUserData();

  }, [userId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="animate-spin text-cyan-400" size={48} />
        <p className="text-gray-400 mt-4">Loading profile...</p>
      </div>
    );
  }
  
  if (!userProfile) {
      return (
          <div className="text-center py-16 glass-card rounded-xl">
              <h3 className="text-xl font-semibold text-white">User Not Found</h3>
              <p className="text-gray-500 mt-2">Could not find a profile for this user.</p>
            </div>
      )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Two-column layout for profile and reputation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div className="glass-card rounded-2xl p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-400 to-purple-400 flex items-center justify-center text-white font-bold text-3xl">
              {userProfile.username ? userProfile.username.substring(0, 2).toUpperCase() : 'U'}
            </div>
            
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white mb-2">{userProfile.username || 'Anonymous User'}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-2">
                  <Mail size={16} />
                  {userProfile.email || 'Email not available'}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar size={16} />
                  Member since {userProfile.createdAt ? new Date(userProfile.createdAt.toDate()).toLocaleDateString() : 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="glass-card rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-cyan-400">{userStats.totalPosts}</div>
              <div className="text-xs text-gray-400 mt-1">Total Posts</div>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-400">{userStats.cfeReports}</div>
              <div className="text-xs text-gray-400 mt-1">CFE Reports</div>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-400">{userStats.blogPosts}</div>
              <div className="text-xs text-gray-400 mt-1">Blog Posts</div>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-400">{userStats.totalLikes}</div>
              <div className="text-xs text-gray-400 mt-1">Total Likes</div>
            </div>
            <div className="glass-card rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{userStats.verifiedReports}</div>
              <div className="text-xs text-gray-400 mt-1">Verified</div>
            </div>
          </div>

          {userStats.totalPosts > 0 && (
            <div className="mt-6 flex flex-wrap gap-3">
              {userStats.totalPosts >= 10 && (
                <div className="glass-card px-4 py-2 rounded-full flex items-center gap-2">
                  <Award size={16} className="text-cyan-400" />
                  <span className="text-sm font-medium text-gray-200">Active Contributor</span>
                </div>
              )}
              {userStats.verifiedReports > 0 && (
                <div className="glass-card px-4 py-2 rounded-full flex items-center gap-2">
                  <Shield size={16} className="text-green-400" />
                  <span className="text-sm font-medium text-gray-200">Trusted Reporter</span>
                </div>
              )}
              {userStats.totalLikes >= 50 && (
                <div className="glass-card px-4 py-2 rounded-full flex items-center gap-2">
                  <TrendingUp size={16} className="text-purple-400" />
                  <span className="text-sm font-medium text-gray-200">Popular Author</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
        </div>

        {/* Reputation Card Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <ReputationCard userId={userId} />
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <FileText className="text-cyan-400" />
          User's Contributions
        </h2>
        
        <div className="space-y-6">
          {posts.length === 0 && !loading ? (
            <div className="text-center py-16 glass-card rounded-xl">
              <h3 className="text-xl font-semibold text-white">No posts yet</h3>
              <p className="text-gray-500 mt-2">This user hasn't created any posts.</p>
            </div>
          ) : (
            posts.map(post => <PostCard key={post.id} post={post} />)
          )}
        </div>
      </div>
    </div>
  );
}